// ── API Client (ported from UniTicket, plan §6) ───────────────
// Fetch wrapper with in-memory JWT access token + silent refresh on 401.
// The access token NEVER touches localStorage; the refresh token lives in an
// httpOnly cookie scoped to /internal/v1/auth.

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export const API_PREFIX = '/internal/v1';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_PREFIX}${path}`;

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  // Let the browser set Content-Type for FormData (multipart boundary)
  if (!(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  let res = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  // 401 with a token → silent refresh, retry once
  if (res.status === 401 && accessToken) {
    const refreshed = await silentRefresh();
    if (refreshed) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      res = await fetch(url, { ...options, headers, credentials: 'include' });
    }
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string; details?: unknown };
    throw new ApiError(res.status, body.message || body.error || res.statusText, body.details);
  }

  return res.json() as Promise<T>;
}

async function silentRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_PREFIX}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) {
      accessToken = null;
      return false;
    }
    const data = (await res.json()) as { accessToken: string };
    accessToken = data.accessToken;
    return true;
  } catch {
    accessToken = null;
    return false;
  }
}
